/**
 * Donation Context Provider
 *
 * Manages donation selection and cart state
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import type { DonationType, CartItem } from '@/types';
import { dollarsToCents } from '@/lib/formatting';

/**
 * Donation context value
 */
interface DonationContextValue {
  // Cart items
  cartItems: CartItem[];

  // Computed values
  totalAmount: number; // Total in cents
  itemCount: number;

  // Actions
  addItem: (donationType: DonationType, customAmount?: number) => void;
  removeItem: (itemId: string) => void;
  updateItemQuantity: (itemId: string, quantity: number) => void;
  updateCustomAmount: (itemId: string, amount: number) => void;
  clearCart: () => void;
}

const DonationContext = createContext<DonationContextValue | undefined>(undefined);

/**
 * Donation Provider Props
 */
interface DonationProviderProps {
  children: React.ReactNode;
}

/**
 * Generate unique cart item ID
 */
function generateCartItemId(): string {
  return `cart-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Donation Provider Component
 *
 * Manages shopping cart for donations
 */
export function DonationProvider({ children }: DonationProviderProps) {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);

  /**
   * Add item to cart
   */
  const addItem = useCallback((donationType: DonationType, customAmount?: number) => {
    const newItem: CartItem = {
      id: generateCartItemId(),
      donationType,
      quantity: 1,
      customAmount: customAmount ? dollarsToCents(customAmount) : undefined,
    };

    setCartItems((prev) => [...prev, newItem]);
  }, []);

  /**
   * Remove item from cart
   */
  const removeItem = useCallback((itemId: string) => {
    setCartItems((prev) => prev.filter((item) => item.id !== itemId));
  }, []);

  /**
   * Update item quantity
   */
  const updateItemQuantity = useCallback(
    (itemId: string, quantity: number) => {
      if (quantity < 1) {
        removeItem(itemId);
        return;
      }

      setCartItems((prev) =>
        prev.map((item) => (item.id === itemId ? { ...item, quantity } : item))
      );
    },
    [removeItem]
  );

  /**
   * Update custom amount for item
   */
  const updateCustomAmount = useCallback((itemId: string, amount: number) => {
    const amountInCents = dollarsToCents(amount);

    setCartItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, customAmount: amountInCents } : item))
    );
  }, []);

  /**
   * Clear all items from cart
   */
  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  /**
   * Calculate total amount in cents
   */
  const totalAmount = cartItems.reduce((sum, item) => {
    // Use custom amount if provided, otherwise custom amount is required
    // since donation types don't have a default amount field
    const itemAmount = item.customAmount || 0;
    return sum + itemAmount * item.quantity;
  }, 0);

  /**
   * Calculate total item count
   */
  const itemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const value: DonationContextValue = {
    cartItems,
    totalAmount,
    itemCount,
    addItem,
    removeItem,
    updateItemQuantity,
    updateCustomAmount,
    clearCart,
  };

  return <DonationContext.Provider value={value}>{children}</DonationContext.Provider>;
}

/**
 * Hook to use donation context
 *
 * @throws Error if used outside DonationProvider
 */
export function useDonation(): DonationContextValue {
  const context = useContext(DonationContext);

  if (context === undefined) {
    throw new Error('useDonation must be used within a DonationProvider');
  }

  return context;
}
